import * as React from 'react'
import { FormWrapper } from './styled'

const { useState } = React

interface FormContextValue<FormValues> {
  values: FormValues
  markValidity: (name: string, valid: boolean) => void
  updateField: (
    name: string,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void
}

const FormContext = React.createContext<FormContextValue<any> | undefined>(
  undefined,
)

export const FormConsumer = FormContext.Consumer

export const useForm = () => {
  const ctx = React.useContext(FormContext)
  if (!ctx) throw new Error('useFormContext must be used within a FormProvider')
  return ctx
}

interface FormProps<FormValues> {
  onSubmit: (values: FormValues) => void
  initialValues: FormValues
  children: React.ReactNode
}

export const Form = <FormValues,>({
  onSubmit,
  initialValues,
  children,
}: FormProps<FormValues>) => {
  const [values, setValues] = useState<FormValues>(initialValues)
  const [invalidValues, setInvalidValues] = useState<string[]>([])

  const markValidity = (name: string, valid: boolean) => {
    const newValues = valid
      ? invalidValues.filter((v) => v !== name)
      : [...invalidValues, name]
    setInvalidValues(newValues)
  }

  const updateField =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      const newValues = {
        ...values,
        [name]: value,
      }
      setValues(newValues)
    }

  const handleSubmit = () => {
    if (invalidValues.length > 1) return
    onSubmit(values)
  }

  const value = {
    values,
    updateField,
    markValidity,
  }

  return (
    <FormContext.Provider value={value}>
      <FormWrapper onSubmit={handleSubmit}>{children}</FormWrapper>
    </FormContext.Provider>
  )
}
